// ============================================================
// 📁 کنار UsersPage.tsx   (فایل جدید)  —  «امنیت من»: پروفایل، پسورد، 2FA، نشست‌ها
// ============================================================
import React, { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, Smartphone, Monitor, Save, LogOut, Copy, Download, CheckCircle } from "lucide-react";
import { uapi, UserType, relTime, fmtDT, copyText, downloadCSV } from "../../services/usersApi";
import QrCode from "../../components/QrCode";
import { Btn, Field, Badge, SectionTitle, Spinner, Empty, PasswordMeter, Modal, toast, dlg, notify, arr } from "./UsersUi";

export function BackupCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  return (
    <Modal title="کدهای پشتیبان" subtitle="هر کد فقط یک‌بار قابل استفاده است. آن‌ها را در جای امن نگه دارید" icon={<ShieldCheck size={22} color="#d4af37" />} width={520} onClose={onClose}
      footer={<>
        <Btn kind="ghost" icon={<Copy size={14} />} onClick={() => { copyText(codes.join("\n")); toast("کپی شد", "ok"); }}>کپی</Btn>
        <Btn kind="ghost" icon={<Download size={14} />} onClick={() => downloadCSV("backup-codes.csv", ["کد پشتیبان"], codes.map((c) => [c]))}>دانلود</Btn>
        <Btn kind="gold" onClick={onClose}>ذخیره کردم</Btn></>}>
      <div className="um-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {codes.map((c) => <div key={c} className="um-card um-code" style={{ padding: 12, textAlign: "center", fontSize: 15, fontWeight: 700 }}>{c}</div>)}
      </div>
      <div className="um-hint" style={{ marginTop: 14 }}>این کدها فقط همین یک‌بار نمایش داده می‌شوند.</div>
    </Modal>
  );
}

export function MySecurity({ me, minLen, twoFaRequired, onChanged }: { me: UserType; minLen: number; twoFaRequired: boolean; onChanged: () => void }) {
  /* پروفایل */
  const [p, setP] = useState({ fullName: me.full_name, phone: me.phone || "" });
  const saveProfile = async () => { const r = await uapi.profileUpdate(p); if (notify(r)) onChanged(); };

  /* پسورد */
  const [pw, setPw] = useState({ cur: "", nw: "", rep: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const changePw = async () => {
    if (pw.nw !== pw.rep) { toast("پسورد جدید و تکرار آن یکی نیست", "err"); return; }
    setPwBusy(true); const r = await uapi.changePassword(pw.cur, pw.nw); setPwBusy(false);
    if (notify(r)) { setPw({ cur: "", nw: "", rep: "" }); loadSessions(); onChanged(); }
  };

  /* 2FA */
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy2, setBusy2] = useState(false);
  const startSetup = async () => { const r = await uapi.twofaStart(); if (r.success) { setSetup({ secret: r.secret, uri: r.uri }); setCode(""); } else notify(r); };
  const enable = async () => { setBusy2(true); const r = await uapi.twofaEnable(code); setBusy2(false); if (r.success) { toast("2FA فعال شد", "ok"); setSetup(null); setCodes(r.backupCodes || []); onChanged(); } else notify(r); };
  const disable = async () => {
    const pass = await dlg.password("غیرفعال‌سازی 2FA", "پسورد خودتان را وارد کنید"); if (!pass) return;
    const c = await dlg.prompt("کد 2FA", "کد ۶ رقمی فعلی (یا یک کد پشتیبان)", "123456", true); if (!c) return;
    const r = await uapi.twofaDisable(pass, c); if (notify(r)) onChanged();
  };
  const regen = async () => {
    const pass = await dlg.password("ساخت کدهای پشتیبان جدید", "کدهای قبلی باطل می‌شوند. پسورد خودتان را وارد کنید"); if (!pass) return;
    const c = await dlg.prompt("کد 2FA", "کد ۶ رقمی فعلی", "123456", true); if (!c) return;
    const r = await uapi.twofaBackupRegen(pass, c); if (r.success) setCodes(r.backupCodes || []); else notify(r);
  };

  /* نشست‌ها */
  const [sess, setSess] = useState<any[] | null>(null);
  const loadSessions = async () => { const r = await uapi.sessions(); setSess(arr(r, "sessions")); };
  useEffect(() => { loadSessions(); }, []);
  const kill = async (id: number) => { const r = await uapi.sessionRevoke(id); if (notify(r)) loadSessions(); };
  const killOthers = async () => { if (!(await dlg.confirm("خروج از دستگاه‌های دیگر", "همه نشست‌ها به‌جز همین دستگاه بسته می‌شود.", true))) return; const others = (sess || []).filter((s) => !s.current); for (const s of others) await uapi.sessionRevoke(s.id); toast("انجام شد", "ok"); loadSessions(); };

  const grid = { gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", alignItems: "start" } as React.CSSProperties;
  return (
    <div className="um-grid" style={grid}>
      <div className="um-card gold">
        <SectionTitle>پروفایل من</SectionTitle>
        <div className="um-grid">
          <Field label="نام کامل"><input className="um-input" value={p.fullName} onChange={(e) => setP({ ...p, fullName: e.target.value })} /></Field>
          <Field label="تلفن"><input className="um-input" dir="ltr" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} /></Field>
          <div className="um-hint">نام کاربری: <b dir="ltr">@{me.username}</b> — ایمیل: <b dir="ltr">{me.email || "—"}</b> (تغییر ایمیل و نقش فقط توسط ادمین)</div>
          <div><Btn icon={<Save size={15} />} onClick={saveProfile}>ذخیره پروفایل</Btn></div>
        </div>
      </div>

      <div className="um-card gold">
        <SectionTitle>تغییر پسورد</SectionTitle>
        <div className="um-grid">
          <Field label="پسورد فعلی"><input className="um-input" type="password" autoComplete="current-password" value={pw.cur} onChange={(e) => setPw({ ...pw, cur: e.target.value })} /></Field>
          <Field label="پسورد جدید"><input className="um-input" type="password" autoComplete="new-password" value={pw.nw} onChange={(e) => setPw({ ...pw, nw: e.target.value })} /><PasswordMeter value={pw.nw} min={minLen} /></Field>
          <Field label="تکرار پسورد جدید"><input className="um-input" type="password" autoComplete="new-password" value={pw.rep} onChange={(e) => setPw({ ...pw, rep: e.target.value })} /></Field>
          <div className="um-hint">پس از تغییر، دستگاه‌های دیگر خارج می‌شوند.</div>
          <div><Btn kind="gold" icon={<KeyRound size={15} />} loading={pwBusy} disabled={!pw.cur || !pw.nw} onClick={changePw}>تغییر پسورد</Btn></div>
        </div>
      </div>

      <div className="um-card gold">
        <SectionTitle>احراز هویت دو مرحله‌ای (2FA)</SectionTitle>
        {me.totp_enabled ? (
          <>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}><CheckCircle size={20} color="#10b981" /><b style={{ color: "#10b981" }}>فعال است</b></div>
            <div className="um-hint">هنگام ورود، علاوه بر پسورد، کد برنامه‌ی احراز هویت نیز لازم است.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              <Btn kind="ghost" size="sm" onClick={regen}>کدهای پشتیبان جدید</Btn>
              {!twoFaRequired && <Btn kind="danger" size="sm" onClick={disable}>غیرفعال‌سازی</Btn>}
            </div>
            {twoFaRequired && <div className="um-hint" style={{ marginTop: 10 }}>طبق سیاست شرکت، 2FA برای شما اجباری است.</div>}
          </>
        ) : setup ? (
          <div className="um-grid">
            <div className="um-hint">۱) در گوشی یکی از برنامه‌های Google Authenticator، Microsoft Authenticator یا Authy را باز کنید و این QR را اسکن کنید.</div>
            <div style={{ display: "flex", justifyContent: "center" }}><QrCode value={setup.uri} size={190} /></div>
            <div className="um-hint">یا کد دستی را وارد کنید:</div>
            <div className="um-card um-code" style={{ padding: 10, textAlign: "center", fontSize: 13, wordBreak: "break-all" }}>{setup.secret}</div>
            <Field label="۲) کد ۶ رقمی نمایش‌داده‌شده"><input className="um-input um-code" inputMode="numeric" maxLength={8} value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="123456" /></Field>
            <div style={{ display: "flex", gap: 8 }}><Btn kind="gold" loading={busy2} disabled={code.length < 6} onClick={enable}>تایید و فعال‌سازی</Btn><Btn kind="ghost" onClick={() => setSetup(null)}>انصراف</Btn></div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}><Badge color="#f59e0b" dot>غیرفعال</Badge>{twoFaRequired && <Badge color="#ef4444">اجباری برای شما</Badge>}</div>
            <div className="um-hint">با فعال‌سازی 2FA حتی اگر پسورد شما لو برود، کسی بدون گوشی شما وارد نمی‌شود.</div>
            <div style={{ marginTop: 14 }}><Btn kind="gold" icon={<Smartphone size={15} />} onClick={startSetup}>فعال‌سازی 2FA</Btn></div>
          </>
        )}
      </div>

      <div className="um-card gold" style={{ gridColumn: "1 / -1" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <SectionTitle>دستگاه‌های وارد‌شده</SectionTitle>
          {!!sess && sess.length > 1 && <Btn kind="danger" size="sm" icon={<LogOut size={14} />} onClick={killOthers}>خروج از دستگاه‌های دیگر</Btn>}
        </div>
        {!sess ? <Spinner /> : sess.length === 0 ? <Empty icon={<Monitor size={36} />} title="نشستی یافت نشد" /> : sess.map((s) => (
          <div key={s.id} className="um-kv" style={{ alignItems: "center" }}>
            <div><b>{s.device}</b> {s.current && <Badge color="#10b981" dot>این دستگاه</Badge>}<div className="um-hint" style={{ margin: 0 }}><span dir="ltr">{s.ip || "—"}</span> — آخرین فعالیت {relTime(s.last_used_at || s.created_at)} — {fmtDT(s.created_at)}</div></div>
            {!s.current && <Btn kind="ghost" size="sm" onClick={() => kill(s.id)}>بستن</Btn>}
          </div>
        ))}
      </div>
      {codes && <BackupCodesModal codes={codes} onClose={() => setCodes(null)} />}
    </div>
  );
}
